import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/store/auth-store';
import { RouterLink } from '@/components/router/router-link';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { authControllerConfirmEmail } from '@/apis/backend/generated/auth/auth';

import { Box, Link, Button, Typography, CircularProgress } from '@mui/material';

import { paths } from 'src/routes/paths';

export const Route = createFileRoute('/auth/confirm')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: search.token as string | undefined,
  }),
  component: ConfirmPage,
});

/**
 * Handles email confirmation via token from the confirmation link.
 * Reads ?token= from URL, calls POST /auth/confirm-email, and on success
 * stores tokens and redirects to onboarding.
 */
function ConfirmPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(true);
      return;
    }

    const confirmEmail = async () => {
      try {
        const { accessToken, refreshToken } = await authControllerConfirmEmail({ token });

        // Store tokens and fetch user
        await useAuthStore.getState().setTokensAndFetchUser(accessToken, refreshToken);

        const pendingTransfer = localStorage.getItem('pendingClientTransferToken');
        if (pendingTransfer) {
          localStorage.removeItem('pendingClientTransferToken');
          navigate({
            to: '/client-transfers/claim/$token',
            params: { token: pendingTransfer },
          } as any);
          return;
        }

        navigate({ to: paths.auth.onboarding });
      } catch (err) {
        console.error('Email confirmation error:', err);
        setError(true);
      }
    };

    confirmEmail();
  }, [token, navigate]);

  if (error) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {t('confirm.expiredTitle')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('confirm.expiredBody')}
        </Typography>

        <Button
          fullWidth
          size="large"
          variant="contained"
          component={RouterLink}
          href={paths.auth.signUp}
          sx={{ mb: 2, py: 1.4 }}
        >
          {t('confirm.backToSignUp')}
        </Button>

        <Link component={RouterLink} href={paths.auth.signIn} sx={{ cursor: 'pointer' }}>
          <Typography variant="body2">{t('links.backToSignIn')}</Typography>
        </Link>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      <CircularProgress sx={{ mb: 2 }} />
      <Typography variant="body1" color="text.secondary">
        {t('confirm.confirming')}
      </Typography>
    </Box>
  );
}
